import { DashboardConfigurationDependencies } from '../../dashboard-config/dashboard-config.model';
import { DashboardDataTransformer } from '../../data_transformers/dashboard_data_transformer';
import { DashboardFilter } from '../../filters/dashboard_filter';
import type { MetricProfile } from '../../dashboard-config/metrics/metric-profile';
import type { DashboardAxes } from './dashboard-axes';

export interface DashboardDefinition<TView = void> {
  dashboardId: string;
  /**
   * Shell package id, stamped from the shell profile (`shellId: shell.id`).
   * Owned by each shell package (e.g. RANKING_SHELL_ID); not a central catalog.
   */
  shellId: string;
  variant?: string;
  datasetName: string;
  /** Resolved analysis × metric × granularity, for families built on `resolveDashboardAxes`. */
  axes?: DashboardAxes;
  view: TView;
  filters: (deps: DashboardConfigurationDependencies) => DashboardFilter<any>[];
  preFilterTransformers: (
    deps: DashboardConfigurationDependencies,
  ) => DashboardDataTransformer[];
  postFilterTransformers: (
    deps: DashboardConfigurationDependencies,
  ) => DashboardDataTransformer[];
  chartTransformers?: (
    deps: DashboardConfigurationDependencies,
  ) => DashboardDataTransformer[];
  exportTransformers?: (
    deps: DashboardConfigurationDependencies,
  ) => DashboardDataTransformer[];
  heading: (
    filterValues: Map<string, any>,
    product?: any,
    deps?: DashboardConfigurationDependencies,
  ) => string;
}

/**
 * One product-catalog row: a metric profile expanded across shell keys.
 * Distinct from `*-metric.profile` / `*-shell.profile` modules.
 * Optional `TProductExtras` carries create-input fields beyond shell+metric
 * (e.g. comparison industries `view`).
 */
export type ProductCatalogEntry<
  TMetric,
  TShell,
  TProductExtras extends object = {},
> = {
  metric: TMetric;
  shells: readonly TShell[];
} & TProductExtras;

/** Expand catalog rows into definitions via a `{ shell, metric, ...extras }` create factory. */
export function expandProductCatalog<
  TMetric,
  TShell,
  TDefinition,
  TProductExtras extends object = {},
>(
  catalog: Iterable<ProductCatalogEntry<TMetric, TShell, TProductExtras>>,
  create: (
    product: {
      shell: NoInfer<TShell>;
      metric: NoInfer<TMetric>;
    } & NoInfer<TProductExtras>,
  ) => TDefinition,
): TDefinition[] {
  return Array.from(catalog).flatMap((entry) => {
    const { shells, metric, ...extras } = entry;
    return shells.map((shell) =>
      create({
        ...(extras as TProductExtras),
        shell,
        metric,
      }),
    );
  });
}

/**
 * A catalog row as a definition factory receives it: shell key and the family's metric profile
 * (`base` plus the family's exceptions). Factories start with `resolveProduct`.
 */
export interface CreateDashboardDefinitionBase<TShell extends string, TMetric extends { base: MetricProfile }> {
  shell: TShell;
  metric: TMetric;
}
